import React, { useEffect, useState, createContext } from 'react';
import { useParams, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { languageCodes, languages, defaultLanguage } from '../i18n';

const SITE_URL = 'https://fxastra.com';

// Pages can force a canonical (e.g. English-only broker pages) via SEO's canonicalUrl prop.
export const CanonicalContext = createContext(() => {});

// Component to handle language-based routing and SEO
export default function LanguageRouter() {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Validate and set language from URL
  useEffect(() => {
    if (lang && languageCodes.includes(lang)) {
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang);
      }
    } else if (lang && !languageCodes.includes(lang)) {
      // Invalid language code, redirect to default
      const newPath = location.pathname.replace(`/${lang}`, '') || '/';
      navigate(newPath, { replace: true });
    }
  }, [lang, i18n, navigate, location.pathname]);

  // Get current path without language prefix
  const getPathWithoutLang = () => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    if (pathParts[0] && languageCodes.includes(pathParts[0])) {
      return '/' + pathParts.slice(1).join('/');
    }
    return location.pathname;
  };

  const currentPath = getPathWithoutLang();
  const currentLang = lang || defaultLanguage;

  // Generate hreflang URLs for all languages
  const generateHreflangUrls = () => {
    return languages.map(language => {
      const langPath = language.code === defaultLanguage 
        ? currentPath 
        : `/${language.code}${currentPath === '/' ? '' : currentPath}`;
      return {
        hreflang: language.hreflang,
        href: `${SITE_URL}${langPath}`
      };
    });
  };

  const hreflangUrls = generateHreflangUrls();
  const canonicalUrl = currentLang === defaultLanguage 
    ? `${SITE_URL}${currentPath}` 
    : `${SITE_URL}/${currentLang}${currentPath === '/' ? '' : currentPath}`;

  useEffect(() => { document.documentElement.lang = currentLang; }, [currentLang]);

  const [override, setOverride] = useState(null);
  useEffect(() => { setOverride(null); }, [location.pathname]);

  return (
    <CanonicalContext.Provider value={setOverride}>
      <link rel="canonical" href={override || canonicalUrl} />
      {!override && hreflangUrls.map(({ hreflang, href }) => (
        <link key={hreflang} rel="alternate" hrefLang={hreflang} href={href} />
      ))}
      {!override && <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${currentPath}`} />}
      <Outlet />
    </CanonicalContext.Provider>
  );
}

// Hook to get language-aware links
export function useLanguageLink() {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  const getLocalizedPath = (path) => {
    if (currentLang === defaultLanguage) {
      return path;
    }
    return `/${currentLang}${path === '/' ? '' : path}`;
  };

  return { getLocalizedPath, currentLang };
}

// Component for language-aware Link
export function LocalizedLink({ to, children, ...props }) {
  const { getLocalizedPath } = useLanguageLink();
  const localizedTo = getLocalizedPath(to);
  
  return (
    <a href={localizedTo} {...props}>
      {children}
    </a>
  );
}
